import { useState, useEffect } from 'react'
import { useStore } from '@store/useStore'
import Header from '@components/Header'
import ChatPanel from '@components/ChatPanel'
import PagesPanel from '@components/PagesPanel'
import SettingsModal from '@components/SettingsModal'
import Onboarding from '@components/Onboarding'
import DisclaimerModal, { hasAcceptedDisclaimer } from '@components/DisclaimerModal'
import { handleMagicLinkCallback, getSession, syncToStripe } from '@services/auth'

export default function App() {
  const [showSettings, setShowSettings] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [initialDomain, setInitialDomain] = useState(null)
  const [authMessage, setAuthMessage] = useState(null)
  const { phase, project } = useStore()

  // Handle magic link callback on mount
  useEffect(() => {
    const result = handleMagicLinkCallback()
    if (result) {
      if (result.success) {
        setAuthMessage({ type: 'success', text: `Connecté en tant que ${result.session.email}` })
      } else {
        setAuthMessage({ type: 'error', text: result.error })
      }
      // Clear message after 5s
      setTimeout(() => setAuthMessage(null), 5000)
    }
  }, [])

  // Check if user needs onboarding - go directly to onboarding (marketing site is separate)
  useEffect(() => {
    const hasOnboarded = localStorage.getItem('emd_onboarded')
    if (!hasOnboarded) {
      setShowOnboarding(true)
    }
  }, [])

  const handleOnboardingComplete = (domain) => {
    setShowOnboarding(false)
    // If a domain was entered in onboarding, pass it to ChatPanel to auto-start
    if (domain) {
      setInitialDomain(domain)
    }
  }

  // Request disclaimer acceptance before first generation
  const requestDisclaimerAcceptance = () => {
    return new Promise((resolve) => {
      if (hasAcceptedDisclaimer()) {
        resolve(true)
        return
      }

      setShowDisclaimer(true)
      // Store resolve function for later
      window.__disclaimerResolve = resolve
    })
  }

  const handleDisclaimerAccept = async () => {
    setShowDisclaimer(false)

    // Sync to Stripe if authenticated
    const session = getSession()
    if (session?.customerId) {
      await syncToStripe({ disclaimer_accepted: new Date().toISOString() })
    }

    // Resolve promise
    if (window.__disclaimerResolve) {
      window.__disclaimerResolve(true)
      delete window.__disclaimerResolve
    }
  }

  const handleDisclaimerDecline = () => {
    setShowDisclaimer(false)
    if (window.__disclaimerResolve) {
      window.__disclaimerResolve(false)
      delete window.__disclaimerResolve
    }
  }

  // Show onboarding wizard for new users
  if (showOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />
  }

  return (
    <div className="h-screen bg-slate-950 text-white flex flex-col overflow-hidden">
      <Header onSettingsClick={() => setShowSettings(true)} />

      {/* Auth notification toast */}
      {authMessage && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-top-2 ${
          authMessage.type === 'success'
            ? 'bg-emerald-500/90 text-white'
            : 'bg-red-500/90 text-white'
        }`}>
          {authMessage.text}
        </div>
      )}

      <main className="flex-1 flex overflow-hidden">
        {/* Left Panel - Pipeline/Pages */}
        <div className="w-[480px] border-r border-slate-800 flex flex-col">
          <PagesPanel />
        </div>

        {/* Right Panel - Chat */}
        <div className="flex-1 flex flex-col min-w-0">
          <ChatPanel
            initialDomain={initialDomain}
            onDomainConsumed={() => setInitialDomain(null)}
            onRequestDisclaimer={requestDisclaimerAcceptance}
          />
        </div>
      </main>

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal onClose={() => setShowSettings(false)} />
      )}

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <DisclaimerModal
          onAccept={handleDisclaimerAccept}
          onDecline={handleDisclaimerDecline}
        />
      )}
    </div>
  )
}
